import * as React from 'react';

import { setupPortalConnection } from 'src/store/connections/connectToStardog';
import {
  Connection,
  coercePortalConnection,
} from 'src/store/connections/types';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { usePortalConnectionsWithDemo } from 'src/utils/hooks/usePortalConnectionsWithDemo';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';

export const usePortalConnection = ({
  connectionId,
  enabled = true,
  key = 'default',
}: { connectionId?: string | null; enabled?: boolean; key?: string } = {}) => {
  const dispatch = useReduxDispatch();
  const portalConnections = usePortalConnectionsWithDemo();
  const defaultPortalConnection = useDefaultPortalConnection();

  // find portal connection via connection id, else fallback to current url index
  let connection: Connection | null = null;
  if (connectionId) {
    const foundConnection = portalConnections?.find(
      ({ id }) => connectionId && id === connectionId
    );
    if (foundConnection) {
      connection = coercePortalConnection(foundConnection);
    }
  }
  if (!connection) {
    connection = defaultPortalConnection;
  }

  const didEnabledChange = useDidValueChange(enabled);
  const didConnectionIdChange = useDidValueChange(connection?.id);
  const attemptConnectionKey = React.useRef('');

  React.useEffect(() => {
    if (
      enabled &&
      (didEnabledChange || didConnectionIdChange) &&
      connection &&
      attemptConnectionKey.current !== key
    ) {
      attemptConnectionKey.current = key;
      dispatch(
        setupPortalConnection(connection, {
          shouldNotNotifySuccess: true,
        })
      );
    }
  }, [
    connection,
    didConnectionIdChange,
    didEnabledChange,
    dispatch,
    enabled,
    key,
  ]);
};
